
export const delay = (fn, timeout) => new Promise((resolve, reject) => {
  setTimeout(() => {
    try {
      resolve(fn());
    } catch (e) {
      reject(e);
    }
  }, timeout);
});

export const until = (name, check, interval = 1000, expire = 10000) => {
  const start = new Date();
  const go = () => {
    if (expire && new Date() - start >= expire) {
      throw new Error(`until: ${name} expired!`);
    }

    const { pass, result } = check();

    if (pass) return Promise.resolve(result);
    return delay(go, interval);
  };

  return new Promise((resolve, reject) => {
    try {
      resolve(go());
    } catch (e) {
      reject(e);
    }
  });
};

export const range = (start, end, step = 1) => {
  const ret = [];

  for (let i = start; i < end; i += step) {
    ret.push(i);
  }

  return ret;
};

export const partial = (fn) => {
  const len = fn.length;
  let arbitary;

  arbitary = (curArgs, leftArgCnt) => (...args) => {
    if (args.length >= leftArgCnt) {
      return fn(...curArgs.concat(args));
    }

    return arbitary(curArgs.concat(args), leftArgCnt - args.length);
  };

  return arbitary([], len);
};

export const reduceRight = (fn, initial, list) => {
  let ret = initial;

  for (let i = list.length - 1; i >= 0; i--) {
    ret = fn(list[i], ret);
  }

  return ret;
};

export const compose = (...args) => reduceRight((cur, prev) => x => cur(prev(x)), x => x, args);

export const map = partial((fn, list) => {
  const result = [];

  for (let i = 0, len = list.length; i < len; i++) {
    result.push(fn(list[i]));
  }

  return result;
});

export const on = partial((key, fn, dict) => {
  if (Array.isArray(dict)) {
    return [
      ...dict.slice(0, key),
      fn(dict[key]),
      ...dict.slice(key + 1)
    ];
  }

  return Object.assign({}, dict, {
    [key]: fn(dict[key])
  });
});

export const updateIn = partial((keys, fn, obj) => {
  const updater = compose(...keys.map(key => on(key)));
  return updater(fn)(obj);
});

export const setIn = partial((keys, value, obj) => {
  const updater = compose(...keys.map(key => on(key)));
  return updater(() => value)(obj);
});

export const getIn = partial((keys, obj) => keys.reduce((prev, key) => {
  if (!prev) return prev;
  return prev[key];
}, obj));

export const pick = partial((keys, obj) => keys.reduce((prev, key) => {
  prev[key] = obj[key];
  return prev;
}, {}));

export const uid = () => `${new Date() * 1}.${
  Math.floor(Math.random() * 10000000).toString(16)}`;

export const flatten = (list) => [].concat.apply([], list);

export const and = (...list) => list.reduce((prev, cur) => prev && cur, true);

export const or = (...list) => list.reduce((prev, cur) => prev || cur, false);

export const intersect = (a, b) => a.filter(item => b.indexOf(item) !== -1);

export const capitalInitial = (str) => str.substr(0, 1).toUpperCase() + str.substr(1).toLowerCase();

export const normalizeFromCamelCase = (camelStr) => {
  const result = [];
  let str = '';

  for (let i = 0, len = camelStr.length; i < len; i++) {
    const c = camelStr.charAt(i);

    if (i > 0 && c >= 'A' && c <= 'Z') {
      result.push(str);
      str = c;
    } else {
      str += c;
    }
  }

  if (str.length > 0) {
    result.push(str);
  }

  return result.map(capitalInitial).join(' ');
};

// Note:
// 1. navigator.geolocation requires https
// 2. navigator.geolocation.getCurrentPosition  doens't work
export const getCurrentPosition = () => new Promise((resolve, reject) => {
  if (!window.navigator.geolocation) {
    throw new Error('geolocation API not supported');
  }

  // Note: For test on laptop only
  // setTimeout(() => {
  //   resolve({
  //     lat: 37.9232755,
  //     lng: 116.3878066
  //   })
  // }, 2000)

  window.navigator.geolocation.getCurrentPosition(
    (pos) => {
      resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude
      });
    },
    (e) => {
      reject(e);
    }
  );
});

// reference: https://stackoverflow.com/questions/12391628/how-can-i-upload-an-embedded-image-with-javascript
export function dataUriToBlob(dataURI) {
  // serialize the base64/URLEncoded data
  let byteString;
  if (dataURI.split(',')[0].indexOf('base64') >= 0) {
    byteString = atob(dataURI.split(',')[1]);
  } else {
    byteString = unescape(dataURI.split(',')[1]);
  }

  // parse the mime type
  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

  // construct a Blob of the image data
  const array = [];
  for (let i = 0; i < byteString.length; i++) {
    array.push(byteString.charCodeAt(i));
  }

  return new Blob(
    [new Uint8Array(array)],
    { type: mimeString }
  );
}

export const checkForm = partial((itemsToCheck, obj, fields) => {
  const result = {};
  const checks = (fields && fields.length)
    ? itemsToCheck.filter(({ related }) => intersect(related, fields).length > 0)
    : itemsToCheck;

  for (let i = 0, len = checks.length; i < len; i++) {
    const args = checks[i].related.map(key => obj[key]);
    const ret = checks[i].check(...args);

    result[checks[i].related[0]] = ret ? null : checks[i].errMsg(checks[i].related);
  }

  return result;
});

export const pad = n => (n >= 10 ? (`${n}`) : (`0${n}`));

export function formatDate(date) {
  const d = new Date(date);

  return [
    d.getFullYear(),
    pad(d.getMonth() + 1),
    pad(d.getDate())
  ]
    .join('-');
}

export function formatTime(date) {
  const d = new Date(date);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function dateDiff(d1, d2) {
  const raw = Math.abs(d1 - d2);
  const diff = Math.floor(raw / 1000);
  const sec = diff % 60;
  const min = Math.floor(diff / 60) % 60;
  const hr = Math.floor(diff / 3600) % 24;
  const day = Math.floor(diff / (3600 * 24));

  return {
    days: day,
    hours: hr,
    minutes: min,
    seconds: sec,
    rawDiff: raw
  };
}

export function daysDiff(d1, d2) {
  return dateDiff(d1, d2).days;
}

export function minDate(...list) {
  const n = Math.min(...list.filter(item => item).map(item => item * 1));
  return new Date(n);
}

export function maxDate(...list) {
  const n = Math.max(...list.filter(item => item).map(item => item * 1));
  return new Date(n);
}

export function coordToTuple(obj) {
  if (Array.isArray(obj)) return obj;
  return [obj.lat, obj.lng];
}

export function coordToObject(obj) {
  if (obj.lat !== undefined && obj.lng !== undefined) {
    return obj;
  }

  return {
    lat: obj[0],
    lng: obj[1]
  };
}

export function coordEqual(a, b) {
  return a.lat === b.lat && a.lng === b.lng;
}

export function objKeyLowerCase(data) {
  if (!data) return data;
  return Object.keys(data).reduce((prev, key) => {
    prev[key.toLowerCase()] = data[key];
    return prev;
  }, {});
}

export const promisify = (fn, context) => (...args) => new Promise((resolve, reject) => {
  fn.apply(context, args.concat((err, data) => {
    if (err) return reject(err);
    return resolve(data);
  }));
});

const phoneReg = /^\+(9[976]\d|8[987530]\d|6[987]\d|5[90]\d|42\d|3[875]\d|2[98654321]\d|9[8543210]|8[6421]|6[6543210]|5[87654321]|4[987654310]|3[9643210]|2[70]|7|1)\d{1,14}$/;
export const validatePhoneNumber = (phone) => phoneReg.test(phone);

export const addCountryCodeToPhoneNumber = (phone) => phone.replace(/^0/, '+27');

export const removeCountryCodeToPhoneNumber = (phone) => phone.replace(/^\+27/, '0');
